import type { GenerationFunction, GenerationResult, GenerationResponse } from './types';

// Import all generation functions
import {
  generateTextToImageGPTImage1,
  generateTextToImageDALLE3,
  generateTextToImageDALLE2,
  generateTextToImageGeminiProVision,
  generateTextToImageGemini15Flash,
  generateTextToImageGemini15Pro,
  generateTextToImageGemini25Pro,
  generateTextToImageGemini25Flash,
  generateTextToImageGemini25FlashImage,
  generateTextToImageNanoBanana,
  generateTextToImageFastSDXL,
  generateTextToImageFluxSchnell,
  generateTextToImageFluxDev,
  generateTextToImageRayFlash2,
  generateTextToImageRay1,
  generateTextToImagePhotonFlash1,
  generateTextToImageSeedReal4,
} from './textToImageHandlers';

import {
  generateImage2ImageGPTImage1,
  generateImage2ImageDALLE3,
  generateImage2ImageDALLE2,
  generateImage2ImageGeminiProVision,
  generateImage2ImageGemini25Pro,
  generateImage2ImageGemini25Flash,
  generateImage2ImageGemini25FlashImage,
  generateImage2ImageNanoBanana,
  generateImage2ImageFastSDXL,
  generateImage2ImageFluxSchnell,
  generateImage2ImageFluxDev,
  generateImage2ImageSeedReal4,
} from './imageToImageHandlers';

import {
  generateText2VideoVeo3,
  generateText2VideoVeo3Direct,
  generateText2VideoHailuo02,
  generateText2VideoT2V01Director,
  generateText2VideoS2V01,
  generateImage2VideoKling,
  generateImage2VideoVeo3Direct,
  generateImage2VideoMiniMaxHailuo02I2V,
  generateImage2VideoMiniMaxHailuo02FastI2V,
  generateImage2VideoHailuo02,
  generateImage2VideoI2V01Director,
  generateImage2VideoS2V01,
  generateText2VideoSora2Pro,
  generateText2VideoSora2,
  generateText2VideoSora1,
  generateText2VideoSoraTurbo,
  generateImage2VideoSora2Pro,
  generateImage2VideoSora2,
  generateImage2VideoSora1,
  generateImage2VideoSoraTurbo,
  generateText2VideoSeedance,
  generateImage2VideoSeedance,
  generateText2VideoHailuoAI,
  generateImage2VideoHailuoAI,
  generateMorphingHailuoAI,
} from './videoHandlers';

export const GENERATION_FUNCTIONS: Record<string, GenerationFunction> = {
  // Text to Image
  'text2image_openai_gpt-image-1': generateTextToImageGPTImage1,
  'text2image_openai_dall-e-3': generateTextToImageDALLE3,
  'text2image_openai_dall-e-2': generateTextToImageDALLE2,
  'text2image_gemini_gemini-pro-vision': generateTextToImageGeminiProVision,
  'text2image_gemini_gemini-1.5-flash': generateTextToImageGemini15Flash,
  'text2image_gemini_gemini-1.5-pro': generateTextToImageGemini15Pro,
  'text2image_gemini_gemini-2.5-pro': generateTextToImageGemini25Pro,
  'text2image_gemini_gemini-2.5-flash': generateTextToImageGemini25Flash,
  'text2image_gemini_gemini-2.5-flash-image-preview': generateTextToImageGemini25FlashImage,
  'text2image_fal_ai_fal-ai/nano-banana/edit': generateTextToImageNanoBanana,
  'text2image_fal_ai_fast-sdxl': generateTextToImageFastSDXL,
  'text2image_fal_ai_flux-schnell': generateTextToImageFluxSchnell,
  'text2image_fal_ai_flux-dev': generateTextToImageFluxDev,
  'text2image_lumalabs_ray-flash-2': generateTextToImageRayFlash2,
  'text2image_lumalabs_ray-1': generateTextToImageRay1,
  'text2image_lumalabs_photon-flash-1': generateTextToImagePhotonFlash1,
  'text2image_lumalabs_photon-1': generateTextToImagePhotonFlash1,
  
  // BytePlus Text to Image generators
  'text2image_byteplus_seedreal-4.0': generateTextToImageSeedReal4,
  
  // Image to Image
  'image2image_openai_gpt-image-1': generateImage2ImageGPTImage1,
  'image2image_openai_dall-e-3': generateImage2ImageDALLE3,
  'image2image_openai_dall-e-2': generateImage2ImageDALLE2,
  'image2image_gemini_gemini-pro-vision': generateImage2ImageGeminiProVision,
  'image2image_gemini_gemini-2.5-pro': generateImage2ImageGemini25Pro,
  'image2image_gemini_gemini-2.5-flash': generateImage2ImageGemini25Flash,
  'image2image_gemini_gemini-2.5-flash-image-preview': generateImage2ImageGemini25FlashImage,
  'image2image_fal_ai_fal-ai/nano-banana/edit': generateImage2ImageNanoBanana,
  'image2image_fal_ai_fast-sdxl': generateImage2ImageFastSDXL,
  'image2image_fal_ai_flux-schnell': generateImage2ImageFluxSchnell,
  'image2image_fal_ai_flux-dev': generateImage2ImageFluxDev,
  
  // BytePlus Image to Image generators
  'image2image_byteplus_seedreal-4.0': generateImage2ImageSeedReal4,
  
  // Text to Video
  'text2video_fal_ai_veo3': generateText2VideoVeo3,
  'text2video_google_veo3': generateText2VideoVeo3Direct,
  'text2video_gemini_veo3': generateText2VideoVeo3Direct, // Alias for Google VEO3
  'text2video_hailuoai_hailuo-02': generateText2VideoHailuo02,
  'text2video_hailuoai_t2v-01-director': generateText2VideoT2V01Director,
  'text2video_hailuoai_s2v-01': generateText2VideoS2V01,
  
  // Image to Video
  'image2video_fal_ai_kling-video': generateImage2VideoKling,
  'image2video_google_veo3': generateImage2VideoVeo3Direct,
  'image2video_gemini_veo3': generateImage2VideoVeo3Direct, // Alias for Google VEO3
  'image2video_fal_ai_minimax-hailuo-02-i2v': generateImage2VideoMiniMaxHailuo02I2V,
  'image2video_fal_ai_minimax-hailuo-02-fast-i2v': generateImage2VideoMiniMaxHailuo02FastI2V,
  'image2video_hailuoai_hailuo-02': generateImage2VideoHailuo02,
  'image2video_hailuoai_i2v-01-director': generateImage2VideoI2V01Director,
  'image2video_hailuoai_s2v-01': generateImage2VideoS2V01,
  
  // Sora Video (OpenAI)
  'text2video_openai_sora-2-pro-1.0': generateText2VideoSora2Pro,
  'text2video_openai_sora-2-1.0': generateText2VideoSora2,
  'text2video_openai_sora-2': generateText2VideoSora2, // Alias for sora-2-1.0
  'text2video_openai_sora-1': generateText2VideoSora1,
  'text2video_openai_sora-turbo': generateText2VideoSoraTurbo,
  'image2video_openai_sora-2-pro-1.0': generateImage2VideoSora2Pro,
  'image2video_openai_sora-2-1.0': generateImage2VideoSora2,
  'image2video_openai_sora-2': generateImage2VideoSora2, // Alias for sora-2-1.0
  'image2video_openai_sora-1': generateImage2VideoSora1,
  'image2video_openai_sora-turbo': generateImage2VideoSoraTurbo,
  
  // BytePlus Video (Seedance)
  'text2video_byteplus_seedance-1.0-pro': generateText2VideoSeedance,
  'image2video_byteplus_seedance-1.0-pro': generateImage2VideoSeedance,
  
  // HailuoAI Direct API
  'text2video_hailuoai_MiniMax-Hailuo-02': generateText2VideoHailuoAI,
  'image2video_hailuoai_MiniMax-Hailuo-02': generateImage2VideoHailuoAI,
  
  // Morphing Video (MiniMax-Hailuo-02)
  'morphing_hailuoai_MiniMax-Hailuo-02': generateMorphingHailuoAI,
} as const;

// ========== HELPER FUNCTIONS ==========

export const createGenerationResult = (
  type: string,
  platform: string,
  model: string,
  prompt: string,
  inputFiles: File[],
  index: number
): GenerationResult => {
  return {
    id: `${platform}-${model}-${Date.now()}-${index}`,
    type,
    platform,
    model,
    prompt,
    inputFiles,
    status: 'generating',
    timestamp: Date.now()
  };
};

export const updateGenerationStatus = (
  results: GenerationResult[],
  id: string,
  status: GenerationResult['status'],
  updates: Partial<GenerationResult> = {}
): GenerationResult[] => {
  return results.map(result => 
    result.id === id 
      ? { ...result, status, ...updates }
      : result
  );
};

export const handleGenerationSuccess = (
  id: string,
  response: GenerationResponse,
  result: GenerationResult
): Partial<GenerationResult> => {
  return {
    imageUrl: response.generated_image_url,
    videoUrl: response.generated_video_url,
    purposeMetadata: result.purposeMetadata,
    // Multi-image support
    imageUrls: response.generated_image_urls,
    generationType: response.generation_type
  };
};

export const handleGenerationError = (error: Error): Partial<GenerationResult> => {
  return {
    error: error.message
  };
};